import React, { createContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ApiFetchError } from '../transport/api/baseFetch';
import { getUserDetails } from '../transport/api/publicApi';
import { logout } from '../transport/api/protectedApi';
import { registerSessionExpiredHandler } from '../transport/api/sessionPolicy';
import type { UserAuthFields } from '@airhead-games/shared/types';
import { createLogger } from '@airhead-games/shared/utilities';

const logger = createLogger('AuthContext');

export interface AuthContextType {
  isAuthInitialized: boolean;
  user: UserAuthFields | null;
  setUser: React.Dispatch<React.SetStateAction<UserAuthFields | null>>;
  error: string | null;
  setError: (error: string | null) => void;
  lastRoomAccent: string;
  setLastRoomAccent: (accent: string) => void;
  displayAccent: string;
  logUserOut: () => Promise<void>;
  refetchUser: () => Promise<UserAuthFields | null>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserAuthFields | null>(null);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRoomAccent, setLastRoomAccent] = useState<string>('en-US');

  const fetchUser = useCallback(async (): Promise<UserAuthFields | null> => {
    try {
      setError(null);
      const res = await getUserDetails();
      if (res.success && res.data.user) {
        logger.info('User fetched successfully');
        setUser(res.data.user);
        setIsAuthInitialized(true);
        return res.data.user;
      }
    } catch (err) {
      if (!(err instanceof ApiFetchError && err.status === 401)) {
        logger.error('Session probe failed', err);
      }
    }

    logger.info('No authenticated user session found');
    setUser(null);
    setIsAuthInitialized(true);
    return null;
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    return registerSessionExpiredHandler(() => {
      logger.info('Session expired — clearing user');
      setUser(null);
      setError(null);
      setIsAuthInitialized(true);
    });
  }, []);

  const logUserOut = useCallback(async () => {
    try {
      logger.info('Logging out user...');
      await logout();
      logger.info('Logout successful');
      setUser(null);
      setError(null);
      toast.success('Logged out successfully');
    } catch (err) {
      logger.error('Logout failed', err);
      setUser(null);
      setError('Failed to logout');
    }
  }, []);

  // Compute effective accent with priority: user accent > last room accent > default
  const displayAccent = React.useMemo(() => {
    return user?.userPreferences?.accent || lastRoomAccent;
  }, [user, lastRoomAccent]);

  const value = React.useMemo(
    () => ({
      isAuthInitialized,
      user,
      setUser,
      error,
      setError,
      lastRoomAccent,
      setLastRoomAccent,
      displayAccent,
      logUserOut,
      refetchUser: fetchUser,
    }),
    [
      user,
      isAuthInitialized,
      error,
      lastRoomAccent,
      displayAccent,
      logUserOut,
      fetchUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
